import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { ProfileSurveys } from 'types/Surveys';
import { getMainAccountId } from 'store/account';
import useFeature from 'services/features/useFeature';

export const postSurveySubmitted = async ({
  tinyaccount_id,
  kit_id,
  form_id,
}: {
  tinyaccount_id: string;
  kit_id?: string;
  form_id: string;
}) => {
  const response = await API.post(
    apiName,
    `${apiBasePath}/tinyAccounts/${tinyaccount_id}/typeform_submit`,
    {
      form_id,
      kit_id: kit_id ?? null,
    },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.SURVEYS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        tinyAccountId: tinyaccount_id,
        kitId: kit_id,
        formId: form_id,
      },
    },
  );
  return response;
};
const useSubmitFinishedSurvey = () => {
  const trackEvent = useAnalytics().trackEvent;
  const queryClient = useQueryClient();
  const mainAccountId = useSelector(getMainAccountId);
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );

  return useMutation({
    mutationFn: (properties: {
      form_id: string;
      tinyaccount_id: string;
      kit_id: string;
    }) => {
      return postSurveySubmitted(properties);
    },
    onMutate: async variables => {
      //we log the event before to ensure it is logged even if the update fails
      trackEvent('Survey Completed', variables);
      const queryKey = [
        apiName,
        `profileSurveys`,
        variables.tinyaccount_id,
        variables.kit_id,
      ];
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      // Snapshot the previous value
      const previousSurveys = queryClient.getQueryData(queryKey);
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldSurveys: ProfileSurveys[]) => {
        return oldSurveys.map(oldSurvey =>
          oldSurvey.typeform_survey_id === variables.form_id
            ? { ...oldSurvey, is_complete: true }
            : oldSurvey,
        );
      });
      // Return the previous value in case of error
      return { previousSurveys };
    },
    onSuccess(data, variables, context) {
      // Invalidate the kits query (to get date_survey_completed)
      const queryKey = ['kits', mainAccountId];
      queryClient.invalidateQueries({ queryKey });
      // No toast in the redesigned flow: finishing a bundle already moves the
      // user to the "next up" transition screen or to the completion screen,
      // so the toast lands on top of a screen that says the same thing.
      // Errors still toast — those have nothing else announcing them.
      if (!isRedesignedActivationEnabled) {
        toast.success('Survey Completed');
      }
    },
    onError: (err, variables, context) => {
      const queryKey = [
        apiName,
        `profileSurveys`,
        variables.tinyaccount_id,
        variables.kit_id,
      ];
      // Roll back the cache to the previous value
      queryClient.setQueryData(queryKey, context?.previousSurveys);
      toast.error(
        'There was an error submitting the survey. Please try again.',
      );
    },
    onSettled: (data, error, variables, context) => {
      const queryKey = [
        apiName,
        `profileSurveys`,
        variables.tinyaccount_id,
        variables.kit_id,
      ];
      // Invalidate the query to refetch the updated data from the backend
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
export default useSubmitFinishedSurvey;
